<template>
  <div class="billboard-content">
    <template v-if="billboard.url">
      <SmartLink
        :native="false"
        :to="billboard.url"
        :target="billboard.target"
        :rel="billboard.rel"
        @click="onBillboardClicked(billboard)"
      >
        <img
          :id="`billboard-${billboard.id}`"
          :alt="billboard.altText"
          class="billboard-image"
          :src="billboard.imgSrc"
        />
      </SmartLink>
    </template>

    <template v-else>
      <img
        :id="`billboard-${billboard.id}`"
        :alt="billboard.altText"
        class="billboard-image"
        :src="billboard.imgSrc"
      />
    </template>

    <div class="terms-and-conditions-container">
      <TermsAndConditions
        v-if="billboard.termsAndConditions"
        :description="billboard.termsAndConditions.description"
        class="tnc-link"
      />
    </div>

    <VideoThumbnail
      v-if="billboard.videoId"
      class="video-thumbnail-container"
      :is-billboard-content="true"
      :thumbnail-alt-text="
        billboard.title[`lng-${$i18n.locale}`] || billboard.title
      "
      :modal-caption="billboard.title[`lng-${$i18n.locale}`] || billboard.title"
      :video-id="billboard.videoId"
      @play-video="onPlayVideo(billboard)"
    />

    <div v-if="billboard.hasMainBanner" class="information-container">
      <div class="information-title">{{ infoBoxTitle }}</div>
      <p>{{ infoBoxDescription }}</p>
    </div>
  </div>
</template>

<script>
import TermsAndConditions from '@sephora-asia/molecules-terms-and-conditions/TermsAndConditions.vue'
import VideoThumbnail from '@sephora-asia/molecules-video-thumbnail/VideoThumbnail.vue'

export default {
  name: 'Billboard',

  components: {
    TermsAndConditions,
    VideoThumbnail
  },

  props: {
    /**
     * Billboard attributes
     */
    billboard: {
      type: Object,
      required: true
    },
    /**
     * Title for information box
     */
    infoBoxTitle: {
      type: String,
      default: ''
    },
    /**
     * Description for information box
     */
    infoBoxDescription: {
      type: String,
      default: ''
    },
    /**
     * Determine if current billboard is active or not
     */
    isActive: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    isActive() {
      // using isMobile here to avoid the tab panel billboard impression being triggered in Mweb
      if (this.isActive && !this.$device.isMobile) {
        this.onBillboardActive(this.billboard)
      }
    }
  },

  methods: {
    onBillboardActive(billboard) {
      this.$analytics.trackEvent('click', {
        params: {
          event_category: 'Brand',
          event_action: 'Banner View',
          event_label: `${billboard.id}|${billboard.altText}`
        }
      })
    },
    onBillboardClicked(billboard) {
      this.$analytics.trackEvent('click', {
        params: {
          event_category: 'Brand',
          event_action: 'Banner Click',
          event_label: `${billboard.id}|${billboard.altText}`
        }
      })
    },
    onPlayVideo(billboard) {
      this.$analytics.trackEvent('click', {
        params: {
          event_category: 'Brand',
          event_action: 'Banner Video',
          event_label: `${billboard.id}|${
            billboard.title[`lng-${this.$i18n.locale}`] || billboard.title
          }`
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.billboard-content {
  position: relative;

  .video-thumbnail-container {
    position: absolute;
    width: 40%;
    height: 100%;
    top: 0;
    right: 0;
    bottom: 0;
    padding: 3% 3% 3% 0;

    ::v-deep .video-thumbnail {
      width: 100%;
      height: 100%;

      .image-container {
        height: 100%;

        img {
          height: 100%;
        }
      }
    }
  }
}

.billboard-image {
  display: block;
  width: 100%;
}

.information-container {
  position: absolute;
  width: 35%;
  top: 0;
  right: 0;
  bottom: 0;
  margin: 4% 2% 4% 0;
  padding: 25px 34px;
  background-color: $white;
  overflow-y: auto;

  .information-title {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 14px;
  }

  p {
    line-height: 16px;
    margin-top: 7px;
  }
}
</style>
